/**
 * TanStack Query hooks for the "Show guided tours" user preference.
 *
 * The preference is stored server-side as `show_guided_tours` on the
 * existing GET/PUT /auth/notifications/preferences endpoint.
 *
 * The query deliberately has no placeholderData so callers can distinguish
 * "still loading" (undefined) from "server said false". This prevents the
 * auto-start effect in TourProvider from firing before the real value arrives.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/services/api/client'

// ── Query key ─────────────────────────────────────────────────────────────────

export const guidedToursKeys = {
  all: ['user-preferences', 'guided-tours'] as const,
}

// ── Response shape (partial — we only need the one field) ─────────────────────

interface NotificationPreferencesResponse {
  show_guided_tours?: boolean
  data?: {
    show_guided_tours?: boolean
  }
}

// ── Read hook ─────────────────────────────────────────────────────────────────

/**
 * Returns the stored `show_guided_tours` preference.
 *
 * - `undefined` while the initial fetch is in-flight (callers must wait).
 * - `true` / `false` once the server has responded.
 * - Falls back to `true` when the field is absent from the response or on
 *   network error (fail-open: don't accidentally suppress tours).
 *
 * Callers that need a definite boolean for display should use `?? true`.
 */
export function useGuidedToursPreference(): {
  showGuidedTours: boolean | undefined
  isLoading: boolean
} {
  const { data, isLoading } = useQuery({
    queryKey: guidedToursKeys.all,
    queryFn: async () => {
      const resp = await apiClient.get<NotificationPreferencesResponse>(
        '/auth/notifications/preferences',
      )
      // F1 cleanup: single-expression deref handles both top-level and nested
      // .data shapes without the confusing double-cast.
      const raw = resp?.data?.show_guided_tours ?? resp?.show_guided_tours
      return raw === undefined ? true : raw
    },
    // No placeholderData — we need undefined while loading so the auto-start
    // gate in TourProvider can distinguish "not yet known" from "server: false".
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  })

  return { showGuidedTours: data, isLoading }
}

// ── Write hook ────────────────────────────────────────────────────────────────

/**
 * Mutation that PUTs `{show_guided_tours: bool}` and invalidates the query.
 *
 * Omitting any other preference keys in the body is safe — the backend
 * treats absent keys as "preserve stored value".
 */
export function useSetGuidedToursPreference() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (showGuidedTours: boolean) =>
      apiClient.put('/auth/notifications/preferences', { show_guided_tours: showGuidedTours }),

    onMutate: async (showGuidedTours) => {
      // Cancel in-flight refetches to avoid overwriting the optimistic update.
      await qc.cancelQueries({ queryKey: guidedToursKeys.all })
      const previous = qc.getQueryData<boolean>(guidedToursKeys.all)
      qc.setQueryData<boolean>(guidedToursKeys.all, showGuidedTours)
      return { previous }
    },

    onError: (_err, _vars, context) => {
      // F2 cleanup: `!= null` covers both null and undefined so an error that
      // fires before the first load (previous === undefined) still restores.
      if (context != null) {
        qc.setQueryData(guidedToursKeys.all, context.previous)
      }
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: guidedToursKeys.all })
    },
  })
}
